import React, { useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import "../../styles/Prices.css";
import ContactSalesModal from "../../components/ContactSalesModal";
import { useEffect } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { trackEvent } from "../../firebase/analytics";
import {
  Users,
  CalendarClock,
  Crown,
  GraduationCap,
  Headset,
  ShieldCheck,
  Clock,
  Lock,
} from "lucide-react";

const TIERS = ["1–3", "4–10", "11–20", "21–35", "36–50"];

const PLANS = [
  {
    name: "Basic",
    tagline: "Get started with the essentials",
    monthly: [0, 15, 30, 60, 90],
    yearly: [0, 171, 342, 684, 1026],
    ctaPlanKey: "basic",
    freeForever: true,
    yearlyDiscount: 5,
    highlights: [
      "Quick hire & self-onboarding",
      "Time tracking",
      "Shift scheduling",
      "Payroll-ready reports",
      "Multi-location support",
    ],
  },
  {
    name: "Standard",
    tagline: "Everything you need for growing teams",
    monthly: [15, 25, 50, 95, 145],
    yearly: [171, 285, 570, 1083, 1653],
    ctaPlanKey: "standard",
    yearlyDiscount: 5,
    highlights: [
      "Everything in Basic",
      "Leave management",
      "Expense management",
      "Task manager",
      "Multi-location support",
    ],
  },
  {
    name: "Premium",
    tagline: "Advanced tools for operations-heavy teams",
    monthly: [25, 40, 80, 150, 225],
    yearly: [285, 456, 912, 1710, 2565],
    ctaPlanKey: "premium",
    popular: true,
    yearlyDiscount: 5,
    highlights: [
      "Everything in Standard",
      "Training manager",
      "Reports & Analytics",
      "IT Helpdesk Ticketing",
      "Multi-location support",
    ],
  },
  {
    name: "Training",
    tagline: "Onboarding, training & compliance",
    monthly: [10, 20, 40, 70, 100],
    yearly: [102, 204, 408, 714, 1020],
    ctaPlanKey: "training",
    yearlyDiscount: 15,
    highlights: [
      "Assign courses (PDFs, videos, links)",
      "Due dates, reminders & progress tracking",
      "Upload & organize training resources",
      "Training analytics",
      "Completion reports",
    ],
  },
  {
    name: "Help Desk",
    tagline: "Ticketing & internal IT support",
    monthly: [10, 20, 40, 70, 100],
    yearly: [102, 204, 408, 714, 1020],
    ctaPlanKey: "helpdesk",
    yearlyDiscount: 15,
    highlights: [
      "Employee IT ticket submission",
      "Prioritize & assign tickets",
      "Statuses, SLAs & internal notes",
      "Attachments & activity log",
      "Helpdesk analytics & exports",
    ],
  },
];

const FEATURES = [
  {
    label: "Quick hire & self-onboarding",
    basic: true,
    standard: true,
    premium: true,
  },
  {
    label: "Time tracking",
    basic: true,
    standard: true,
    premium: true,
  },
  {
    label: "Shift scheduling",
    basic: true,
    standard: true,
    premium: true,
  },
  {
    label: "Payroll-ready reports",
    basic: true,
    standard: true,
    premium: true,
  },
  {
    label: "Leave management",
    basic: false,
    standard: true,
    premium: true,
  },
  {
    label: "Expense management",
    basic: false,
    standard: true,
    premium: true,
  },
  {
    label: "Task manager",
    basic: false,
    standard: true,
    premium: true,
  },
  {
    label: "Training manager",
    basic: false,
    standard: false,
    premium: true,
  },
  {
    label: "Reports & Analytics",
    basic: false,
    standard: false,
    premium: true,
  },
  {
    label: "IT Helpdesk Ticketing",
    basic: false,
    standard: false,
    premium: true,
  },
  {
    label: "Multi-location support",
    basic: true,
    standard: true,
    premium: true,
  },
];

const fmt = (n) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

const PLAN_ICONS = {
  Basic: Users,
  Standard: CalendarClock,
  Premium: Crown,
  Training: GraduationCap,
  "Help Desk": Headset,
};

export default function Prices() {
  const [billing, setBilling] = useState("monthly");
  const [showContact, setShowContact] = useState(false);
  const headerCopy = useMemo(
    () =>
      billing === "monthly"
        ? { suffix: "/mo", note: "Billed monthly" }
        : { suffix: "/yr", note: "Billed annually" },
    [billing],
  );

  useEffect(() => {
    trackEvent("pricing_page_view", {
      page: "pricing",
    });
  }, []);
  return (
    <>
      <Navbar />

      <main className="prices-page">
        <header className="prices-header">
          <h1>
            Simple pricing. <br />
            Powerful <span>results.</span>
          </h1>

          <p className="subtitle">
            Pick a plan and team size. Switch tiers anytime no contracts, no
            hidden fees.
          </p>

          <div className="billing-toggle" role="tablist" aria-label="Billing">
            <span className="toggle-label">Pay monthly</span>

            <button
              role="tab"
              aria-selected={billing === "monthly"}
              className={`billing-switch ${
                billing === "yearly" ? "yearly" : "monthly"
              }`}
              onClick={() => {
                const newBilling = billing === "monthly" ? "yearly" : "monthly";

                trackEvent("pricing_billing_toggle", {
                  billing_cycle: newBilling,
                });

                setBilling(newBilling);
              }}
              type="button"
            >
              <span />
            </button>

            <span className="toggle-label">Pay yearly</span>
            <span className="save-pill">Save up to 15%</span>
          </div>

          <div className="billing-note">{headerCopy.note}</div>
        </header>

        <section className="plans-section">
          <div className="plans-grid">
            {PLANS.map((p) => {
              const prices = p[billing];
              const startFrom = prices?.[0] ?? 0;

              return (
                <article
                  key={p.name}
                  className={`plan-card ${p.popular ? "popular" : ""}`}
                >
                  {p.popular && <div className="badge">Most Popular</div>}

                  <header className="plan-head">
                    <div className={`plan-icon plan-icon-${p.ctaPlanKey}`}>
                      {(() => {
                        const Icon = PLAN_ICONS[p.name] || FaCheck;
                        return <Icon size={24} strokeWidth={2.4} />;
                      })()}
                    </div>

                    <h3>{p.name}</h3>
                    <p className="tagline">{p.tagline}</p>

                    <div className="starting">
                      <span className="from">
                        {p.freeForever ? "Free for up to 3 employees" : "From"}
                      </span>
                      <span className="price">
                        {fmt(startFrom)}
                        <span className="per">{headerCopy.suffix}</span>
                      </span>
                    </div>
                  </header>

                  <table className="tier-table" aria-label={`${p.name} tiers`}>
                    <thead>
                      <tr>
                        <th>Team size</th>
                        <th>Price {headerCopy.suffix}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {TIERS.map((t, i) => (
                        <tr key={t}>
                          <td>{t} employees</td>
                          <td>{fmt(prices[i])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <ul className="highlights">
                    {p.highlights.map((h) => (
                      <li key={h}>
                        <FaCheck className="check-ico" />
                        {h}
                      </li>
                    ))}
                  </ul>

                  <a
                    href={`/register?plan=${encodeURIComponent(
                      p.ctaPlanKey,
                    )}&billing=${billing}`}
                    className="cta"
                    onClick={() => {
                      trackEvent("pricing_plan_click", {
                        plan: p.name,
                        billing_cycle: billing,
                      });
                    }}
                  >
                    {p.name === "Basic" ? "Start Free" : "Get Started"}
                  </a>
                </article>
              );
            })}
          </div>
        </section>

        <section className="compare-section">
          <div className="compare-table">
            <table>
              <thead>
                <tr>
                  <th>Features</th>
                  <th>Basic</th>
                  <th>Standard</th>
                  <th>Premium</th>
                </tr>
              </thead>

              <tbody>
                {FEATURES.map((row) => (
                  <tr key={row.label}>
                    <td className="feature-name">{row.label}</td>

                    <td className="cell-center">
                      {row.basic ? (
                        <FaCheck className="check" />
                      ) : (
                        <FaTimes className="cross" />
                      )}
                    </td>

                    <td className="cell-center">
                      {row.standard ? (
                        <FaCheck className="check" />
                      ) : (
                        <FaTimes className="cross" />
                      )}
                    </td>

                    <td className="cell-center">
                      {row.premium ? (
                        <FaCheck className="check" />
                      ) : (
                        <FaTimes className="cross" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="pricing-benefits">
          <div className="benefit-item">
            <div className="benefit-icon green">
              <ShieldCheck size={28} strokeWidth={2.2} />
            </div>

            <strong>Free Plan Available</strong>

            <p>
              Manage up to 3 employees for free. Upgrade when your business
              grows.
            </p>
          </div>

          <div className="benefit-item">
            <div className="benefit-icon blue">
              <Clock size={28} strokeWidth={2.2} />
            </div>
            <strong>Cancel anytime</strong>
            <p>No contracts. No commitments.</p>
          </div>

          <div className="benefit-item">
            <div className="benefit-icon purple">
              <Lock size={28} strokeWidth={2.2} />
            </div>
            <strong>Secure & reliable</strong>
            <p>Enterprise-grade security you can trust.</p>
          </div>

          <div className="benefit-item">
            <div className="benefit-icon red">
              <Headset size={28} strokeWidth={2.2} />
            </div>
            <strong>We’re here to help</strong>
            <p>Fast, friendly support whenever you need it.</p>
          </div>
        </section>

        <section className="pricing-bottom-cta">
          <div>
            <h3>Not sure which plan is right for you?</h3>
            <p>Our team can help you find the perfect fit.</p>
          </div>

          <button
            type="button"
            className="sales-btn"
            onClick={() => {
              trackEvent("pricing_contact_sales_click", {
                source: "pricing_page",
              });
              setShowContact(true);
            }}
          >
            Request Demo
          </button>
        </section>
        {showContact && (
          <ContactSalesModal
            open={showContact}
            source="pricing"
            onClose={() => setShowContact(false)}
          />
        )}
      </main>
    </>
  );
}
